import React, { useMemo } from "react";
import { map } from "lodash";
import LineChart from "@charty/line-plot";
import { Color } from "@glossy";
import { AbstractDate } from "@linkfluence/models/date";
import { Metric } from "@linkfluence/models/metric";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { DatavizLineChart } from "@radarly/models/dataviz";
import { SearchFragmentDateMetrics } from "@radarly/models/search";
import { useRangeWithTimeUnit, useTimezoneFromFragment } from "../../data";
import Empty from "../../empty";
import LineChartTooltip from "../../tooltip/line-chart";
import Tooltip from "../../tooltip/metric";
export interface SharedWidgetsMetricsLineChartInnerProps {
  data: SearchFragmentDateMetrics["data"];
  loading: boolean;
  fragment: SearchFragmentDateMetrics;
  dataviz: DatavizLineChart;
  onClickPoint?: (
    uid: string | number | Array<string | number>,
    range: { from: Date; to: Date }
  ) => void;
  className?: string;
  disableAnimation?: boolean;
  nowDate?: AbstractDate;
  isFullscreen?: boolean;
}

function getLineChartData(
  buckets: SearchFragmentDateMetrics["data"]["aggs"]["date"]["values"],
  metric: Metric
) {
  return map(buckets, (bucket) => {
    if (bucket) {
      return {
        date: bucket.key,
        count: bucket.counts[metric],
      };
    } else {
      return null;
    }
  });
}

function getColor(metric: Metric) {
  return {
    [Metric.Doc]: Color.PacificBlue,
    [Metric.Repost]: Color.Ruby,
    [Metric.Reach]: Color.Martinique,
    [Metric.Impression]: Color.Bermuda,
    [Metric.Engagement]: Color.Bisque,
  }[metric];
}

export default function SharedWidgetsMetricsLineChartInner({
  loading,
  data,
  fragment,
  dataviz,
  onClickPoint,
  className,
  disableAnimation,
  nowDate,
  isFullscreen,
}: SharedWidgetsMetricsLineChartInnerProps) {
  const range = useRangeWithTimeUnit(fragment, loading, nowDate);
  const locale = useCurrentUserLocale();
  const metrics = fragment.request.aggs.date.metrics;
  const series = useMemo(() => {
    const availableSeries = map(metrics, (metric) => {
      return {
        id: metric,
        label: metric,
        data: getLineChartData(data ? data.aggs.date.values : null, metric),
        color: getColor(metric),
      };
    });
    return availableSeries;
  }, [data, metrics]);
  const timezone = useTimezoneFromFragment(fragment);
  return (
    <Empty empty={!series.length}>
      <LineChart
        chartTooltip={(series) => (
          <LineChartTooltip
            dataSeries={series}
            locale={locale}
            timezone={timezone}
          />
        )}
        timezone={timezone}
        series={series}
        scale={dataviz.scale || "linear"}
        range={range}
        locale={locale}
        className={className}
        onClickPoint={onClickPoint}
        tooltip={(serie) => (
          <Tooltip
            range={range}
            count={serie.count}
            color={serie.color}
            label={serie.label}
            metric={metrics[0]}
            locale={locale}
          />
        )}
        disableAnimation={disableAnimation}
        config={{
          hover: { mode: "index", intersect: true },
          plugins: {
            tooltip: { mode: "index", intersect: true, position: "nearest" },
          },
        }}
        isFullscreen={isFullscreen}
      />
    </Empty>
  );
}
